import { AxiosResponse } from 'axios';
import { ListPayload, SuccessResponse } from 'core/http/response';
import config from 'config';
import axiosClient from 'config/axios';
import {
    DocumentResponse,
    GetInvoiceFilesResponse,
    GetInvoicesHistoryParams,
    Invoice,
    InvoiceHistoryRecord,
    InvoiceStatusCountResponse,
    ListInvoicesParams,
    SendInvoiceToCustomerPayload,
    SendInvoiceToCustomerResponse,
    UpdateInvoicePayload,
    UpdateInvoiceStatusPayload,
} from './types';

export const getInvoiceById = async (invoiceId: string): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/${invoiceId}`
    );
    return response.data.payload;
};

export const updateInvoice = async (invoiceId: string, payload: UpdateInvoicePayload): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.put(
        `${config.declarationsUrl}/invoice/${invoiceId}`,
        payload
    );
    return response.data.payload;
};

export const duplicateInvoice = async (invoiceId: string): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.post(
        `${config.declarationsUrl}/invoice/${invoiceId}/duplicate`
    );
    return response.data.payload;
};

// Generates a CSV with the provided invoices
export const exportCsv = async (invoiceIds: string[]): Promise<DocumentResponse> => {
    const response: AxiosResponse<SuccessResponse<DocumentResponse>> = await axiosClient.post(
        `${config.declarationsUrl}/invoice/export-csv`,
        invoiceIds
    );
    return response.data.payload;
};

export const downloadInvoicesPdfZip = async (invoiceIds: string[]): Promise<DocumentResponse> => {
    const response: AxiosResponse<SuccessResponse<DocumentResponse>> = await axiosClient.post(
        `${config.declarationsUrl}/invoice/download-pdf`,
        invoiceIds
    );
    return response.data.payload;
};

export const downloadInvoicePdf = async (invoiceId: string): Promise<DocumentResponse> => {
    const response: AxiosResponse<SuccessResponse<DocumentResponse>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/${invoiceId}/download-pdf`
    );
    return response.data.payload;
};

export const listInvoices = async (params?: ListInvoicesParams): Promise<ListPayload<Invoice>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<Invoice>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/list`,
        {
            params,
        }
    );

    return response.data.payload;
};

export const archiveInvoice = async (invoiceId: string): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.patch(
        `${config.declarationsUrl}/invoice/archive/${invoiceId}`
    );
    return response.data.payload;
};

export const unarchiveInvoice = async (invoiceId: string): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.patch(
        `${config.declarationsUrl}/invoice/unarchive/${invoiceId}`
    );
    return response.data.payload;
};

export const getInvoicesDashboardData = async (params?: {
    archived?: boolean;
}): Promise<ListPayload<InvoiceStatusCountResponse>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<InvoiceStatusCountResponse>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/status-count`,
        { params }
    );
    return response.data.payload;
};

export const sendInvoiceToCustomerForPayment = async (invoiceId: string): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.put(
        `${config.declarationsUrl}/invoice/${invoiceId}/send`
    );
    return response.data.payload;
};

export const getInvoiceHistory = async (
    invoiceId: string,
    params?: GetInvoicesHistoryParams
): Promise<ListPayload<InvoiceHistoryRecord>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<InvoiceHistoryRecord>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/${invoiceId}/history`,
        { params }
    );
    return response.data.payload;
};

export const getPdfForDraftInvoice = async (invoiceId: string, payload: UpdateInvoicePayload): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await axiosClient.post(
        `${config.declarationsUrl}/invoice/download-pdf-draft/${invoiceId}`,
        payload,
        { responseType: 'blob' }
    );
    return response.data;
};

export const getCsvForDraftInvoice = async (invoiceId: string, payload: UpdateInvoicePayload): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await axiosClient.post(
        `${config.declarationsUrl}/invoice/export-csv-draft/${invoiceId}`,
        payload,
        { responseType: 'blob' }
    );
    return response.data;
};

export const listInvoicesByJobId = async (
    jobId: string,
    params?: ListInvoicesParams
): Promise<ListPayload<Invoice>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<Invoice>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/job/${jobId}`,
        { params }
    );
    return response.data.payload;
};

export const getInvoicesStatusCountsByJobId = async (
    jobId: string,
    archived?: boolean
): Promise<ListPayload<InvoiceStatusCountResponse>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<InvoiceStatusCountResponse>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/status-count/job/${jobId}`,
        {
            params: {
                archived,
            },
        }
    );

    return response.data.payload;
};

export const getInvoiceFiles = async (invoiceId: string): Promise<GetInvoiceFilesResponse> => {
    const response: AxiosResponse<SuccessResponse<GetInvoiceFilesResponse>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/${invoiceId}/send-to-customer/file-details`
    );
    return response.data.payload;
};

export const sendInvoiceToCustomer = async (
    invoiceId: string,
    payload: SendInvoiceToCustomerPayload
): Promise<SendInvoiceToCustomerResponse> => {
    const response: AxiosResponse<SuccessResponse<SendInvoiceToCustomerResponse>> = await axiosClient.post(
        `${config.declarationsUrl}/invoice/${invoiceId}/send-to-customer`,
        payload
    );
    return response.data.payload;
};

export const getCustomerInvoicesStatusCounts = async (
    customerId: string,
    archived?: boolean
): Promise<ListPayload<InvoiceStatusCountResponse>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<InvoiceStatusCountResponse>>> = await axiosClient.get(
        `${config.declarationsUrl}/invoice/status-count/customer`,
        {
            params: {
                customerId,
                archived,
            },
        }
    );

    return response.data.payload;
};

export const updateInvoiceStatus = async (invoiceId: string, payload: UpdateInvoiceStatusPayload): Promise<Invoice> => {
    const response: AxiosResponse<SuccessResponse<Invoice>> = await axiosClient.patch(
        `${config.declarationsUrl}/invoice/${invoiceId}/manual-status`,
        payload
    );
    return response.data.payload;
};
